import { createContext, useCallback, useContext, useRef, useSyncExternalStore } from 'react';
import eventBus from '@sas/nova-core/eventBus';
import { toggleFeatureEvent } from '../../components/FeatureFlagManager/consts';
const FeatureFlagDataProviderContext = /*#__PURE__*/createContext({
  isFeatureEnabled: () => Promise.resolve(false)
});
export const useFeatureFlagDataProvider = () => {
  return useContext(FeatureFlagDataProviderContext);
};
export const FeatureFlagProvider = FeatureFlagDataProviderContext.Provider;

/**
 * Hook to get the value of a feature flag. If this hook is used outside of a FeatureFlagProvider, it will always return false
 * unless a data provider is provided. This means that if this hook is used in an App.tsx file outside of AppRoot, it will always
 * return false unless a data provider is provided.
 *
 * @param featureFlagId The id of the feature flag to check
 * @param dataProvider Optional data provider to use for fetching feature flag values. If not provided, the hook will use the
 * default data provider from the nearest FeatureFlagProvider ancestor. When calling this hook from a component that is not a
 * descendant of an AppRoot component, you must provide a data provider.
 */

export const useFeatureFlag = (featureFlagId, dataProvider) => {
  const dp = dataProvider ?? useFeatureFlagDataProvider();
  const ffValue = useRef(false);
  const getSnapshot = useCallback(() => ffValue.current, []);
  const subscriber = useCallback(onChange => {
    const listener = async (_, eventData) => {
      if (eventData?.data === featureFlagId) {
        ffValue.current = await dp.isFeatureEnabled?.(featureFlagId);
        onChange();
      }
    };

    // initialize value from async data provider
    (async () => {
      if (await dp.isFeatureEnabled?.(featureFlagId)) {
        ffValue.current = true;
        onChange();
      }
    })();
    eventBus.subscribe(toggleFeatureEvent, listener);
    return () => eventBus.unsubscribe(toggleFeatureEvent, listener);
  }, [dp, featureFlagId]);
  return useSyncExternalStore(subscriber, getSnapshot);
};
//# sourceMappingURL=useFeatureFlag.js.map